import type { Route } from './components/Shell.tsx';
import { ADMIN_SECTIONS, type AdminSection } from './screens/Admin.tsx';

/**
 * Routing, in about forty lines.
 *
 * A router library would be a dependency, a bundle and an abstraction for six
 * screens. What this application actually needs is a URL that survives a
 * refresh — so a customer can bookmark an installation in progress and come
 * back to it — and that is `history.pushState` plus a parser.
 */

export function parseRoute(pathname: string): Route {
  const segments = pathname
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean);
  const [first, second] = segments;

  switch (first) {
    case undefined:
      return { name: 'dashboard' };
    case 'servers':
      return second === 'new' ? { name: 'addServer' } : { name: 'servers' };
    case 'deployments':
      if (second === 'new') return { name: 'newDeployment' };
      if (second) return { name: 'deployment', id: second };
      return { name: 'deployments' };
    case 'backups':
      return { name: 'backups' };
    case 'monitoring':
      return { name: 'monitoring' };
    case 'settings':
      return { name: 'settings' };
    case 'a':
      // `/a/<token>` — short, because it is pasted into a chat and tapped on
      // a phone. A missing token falls through to the dashboard below.
      if (second) return { name: 'access', token: second };
      return { name: 'dashboard' };
    case 'admin': {
      // `/admin`, `/admin/<section>`, `/admin/customers/<id>`. An unknown
      // section is the overview rather than a dead end, exactly as an unknown
      // path is the dashboard.
      const section = (ADMIN_SECTIONS as readonly string[]).includes(second ?? '')
        ? (second as AdminSection)
        : 'dashboard';
      const third = segments[2];
      return third && section === 'customers'
        ? { name: 'admin', section, customerId: third }
        : { name: 'admin', section };
    }
    default:
      // An unknown path is not worth a "not found" screen in an application
      // this small; home is a better answer than a dead end.
      return { name: 'dashboard' };
  }
}

export function routeToPath(route: Route): string {
  switch (route.name) {
    case 'dashboard':
      return '/';
    case 'servers':
      return '/servers';
    case 'addServer':
      return '/servers/new';
    case 'deployments':
      return '/deployments';
    case 'newDeployment':
      return '/deployments/new';
    case 'deployment':
      return `/deployments/${encodeURIComponent(route.id)}`;
    case 'backups':
      return '/backups';
    case 'monitoring':
      return '/monitoring';
    case 'settings':
      return '/settings';
    case 'access':
      return `/a/${route.token}`;
    case 'admin':
      return route.customerId
        ? `/admin/customers/${encodeURIComponent(route.customerId)}`
        : `/admin/${route.section}`;
  }
}

export function routeTitle(route: Route): string {
  return routeToPath(route);
}
